/**
 * Minyanim Roll engine
 * -------------------------------------------------------------------------
 * Pure, framework-agnostic implementation of the "Minyanim Roll" spec:
 *
 *   1. Roll Types (Scheduling Logic) — decide which rolls get "pulled" into
 *      the Active list for the current clock tick.
 *   2. Priority Logic (Overwriting)  — "Greatest Wins": when two minyanim
 *      share a time slot, only the one from the higher-priority roll shows.
 *   3. Display Toggles               — Bruches (+5 "Start Shacharit") and
 *      Hoidy (+15 "Hodu") secondary timestamps per minyan.
 *
 * The functions here take plain data + injected helpers (e.g. how to resolve
 * a minyan's time to minutes) so they can run identically on the live display,
 * in the editor preview, or in tests.
 */

/** Canonical roll types. Legacy values (`weekday`, `shabbos`) map to the
 *  "Always" variants for backward compatibility. */
export enum RollType {
  WeekdayAlways = 'weekday',
  WeekdayStrict = 'weekday_strict',
  WeekdayCustom = 'weekday_custom',
  ShabbosAlways = 'shabbos',
  ShabbosStrict = 'shabbos_strict',
  RoshChodesh = 'rosh_chodesh',
  YomTov = 'yom_tov',
  Custom = 'custom',
}

/** Options shown in the roll editor, in spec order (1-5 + extras). */
export const ROLL_TYPE_OPTIONS: Array<{ value: RollType; label: string; defaultPriority: number }> = [
  { value: RollType.WeekdayAlways, label: 'Weekday (Always)', defaultPriority: 1 },
  { value: RollType.WeekdayStrict, label: 'Weekday (Strict)', defaultPriority: 2 },
  { value: RollType.WeekdayCustom, label: 'Weekday (Custom)', defaultPriority: 2 },
  { value: RollType.ShabbosAlways, label: 'Shabbos (Always)', defaultPriority: 3 },
  { value: RollType.ShabbosStrict, label: 'Shabbos (Strict)', defaultPriority: 4 },
  { value: RollType.RoshChodesh, label: 'Rosh Chodesh', defaultPriority: 5 },
];

/** Default display-toggle offsets (overridable per minyan). */
export const BRUCHES_OFFSET_MINUTES = 5;
export const HOIDY_OFFSET_MINUTES = 15;
export const ASHREI_OFFSET_MINUTES = 5;
export const BORCHY_OFFSET_MINUTES = 20;
/** Netz is anchored to the real sunrise zman, so its default offset is Exact. */
export const NETZ_OFFSET_MINUTES = 0;

const MINUTES_PER_DAY = 24 * 60;

/**
 * The minute-of-day the roll display treats as the start of the day. A roll's
 * minyanim are ordered relative to this "midnight" instead of 00:00, so a minyan
 * scheduled before it (e.g. 1:59 AM) sorts after the whole rest of the day
 * rather than leading it. See {@link toRollDaySortKey}.
 */
export const ROLL_DAY_START_MINUTES = 2 * 60; // 02:00

const FRIDAY = 5; // JS Date.getDay(): Sun=0 … Sat=6
/** Friday 12:30 PM expressed as minutes-of-week (Sunday 00:00 = 0). */
const FRIDAY_1230_MINUTES_OF_WEEK = FRIDAY * MINUTES_PER_DAY + (12 * 60 + 30);
/** Sunday 00:00 of the following week = end of Saturday. */
const WEEK_END_MINUTES_OF_WEEK = 7 * MINUTES_PER_DAY;

export interface RollContext {
  /** The moment being evaluated. */
  now: Date;
  /** Day of week, Sun=0 … Sat=6. */
  day: number;
  /** Minutes since midnight for `now`. */
  minutesOfDay: number;
  /** Minutes since Sunday 00:00 (day * 1440 + minutesOfDay). */
  minutesOfWeek: number;
  /** True when it is currently Shabbos (Fri after candle-lighting → Sat nightfall). */
  isShabbos: boolean;
  /** True on Rosh Chodesh. */
  isRoshChodesh: boolean;
  /** True on Yom Tov. */
  isYomTov: boolean;
}

/**
 * Build the context the RollType filters operate on. `isShabbos`,
 * `isRoshChodesh` and `isYomTov` are supplied by the caller because they
 * depend on zmanim / the Jewish calendar, which live in the display layer.
 */
export function buildRollContext(input: {
  now: Date;
  isShabbos?: boolean;
  isRoshChodesh?: boolean;
  isYomTov?: boolean;
  // Weekday and minutes-of-day to schedule against, when the caller resolves
  // them somewhere other than the device clock — the display passes the
  // organization's timezone so a screen abroad still switches rolls on the
  // shul's wall clock. Omitted: the device's own local time.
  day?: number;
  minutesOfDay?: number;
}): RollContext {
  const now = input.now instanceof Date && !Number.isNaN(input.now.getTime()) ? input.now : new Date();
  const day =
    Number.isInteger(input.day) && (input.day as number) >= 0 && (input.day as number) <= 6
      ? (input.day as number)
      : now.getDay();
  const minutesOfDay =
    Number.isFinite(input.minutesOfDay) &&
    (input.minutesOfDay as number) >= 0 &&
    (input.minutesOfDay as number) < MINUTES_PER_DAY
      ? Math.floor(input.minutesOfDay as number)
      : now.getHours() * 60 + now.getMinutes();
  return {
    now,
    day,
    minutesOfDay,
    minutesOfWeek: day * MINUTES_PER_DAY + minutesOfDay,
    // Fallback used only when the caller cannot compute real Shabbos: treat
    // the Friday-12:30 boundary as the changeover (matches the Strict rows).
    isShabbos:
      input.isShabbos !== undefined
        ? Boolean(input.isShabbos)
        : day * MINUTES_PER_DAY + minutesOfDay >= FRIDAY_1230_MINUTES_OF_WEEK,
    isRoshChodesh: Boolean(input.isRoshChodesh),
    isYomTov: Boolean(input.isYomTov),
  };
}

/** Map any stored/legacy value to a canonical {@link RollType}. */
export function normalizeRollType(type: unknown): RollType {
  const raw = String(type ?? '').trim().toLowerCase();
  switch (raw) {
    case 'weekday_strict':
    case 'weekday-strict':
    case 'weekdaystrict':
      return RollType.WeekdayStrict;
    case 'weekday_custom':
    case 'weekday-custom':
    case 'weekdaycustom':
      return RollType.WeekdayCustom;
    case 'shabbos_strict':
    case 'shabbos-strict':
    case 'shabbosstrict':
    case 'shabbat_strict':
      return RollType.ShabbosStrict;
    case 'shabbos':
    case 'shabbat':
      return RollType.ShabbosAlways;
    case 'rosh_chodesh':
    case 'rosh-chodesh':
    case 'roshchodesh':
      return RollType.RoshChodesh;
    case 'yom_tov':
    case 'yomtov':
      return RollType.YomTov;
    case 'custom':
      return RollType.Custom;
    case 'weekday':
    case '':
    default:
      return RollType.WeekdayAlways;
  }
}

/**
 * RollType scheduling filter — implements the spec's "Developer Logic":
 *
 *   1 Weekday (Always)  → !isShabbos
 *   2 Weekday (Strict)  → day >= SUNDAY && time < FRIDAY 12:30 PM
 *   3 Shabbos (Always)  → always on (times lock to the upcoming Shabbos and
 *                         regenerate each Sunday 00:00 local — see the Shabbos
 *                         Weekly Static Lock anchor in Display.vue)
 *   4 Shabbos (Strict)  → time >= FRIDAY 12:30 PM && time < SUNDAY 00:00
 *   5 Rosh Chodesh      → jewishCalendar.isRoshChodesh()
 */
export function isRollActiveForType(type: unknown, ctx: RollContext): boolean {
  switch (normalizeRollType(type)) {
    case RollType.WeekdayAlways:
      return !ctx.isShabbos;
    case RollType.WeekdayStrict:
      return ctx.minutesOfWeek >= 0 && ctx.minutesOfWeek < FRIDAY_1230_MINUTES_OF_WEEK;
    case RollType.ShabbosAlways:
      // "Always" means always displayed, not only during Shabbos. The Shabbos
      // times it shows are locked to the upcoming weekend and recalculated once
      // a week (Sunday 00:00 local), handled by the anchor logic in Display.vue.
      // Use Shabbos (Strict) to limit visibility to the live Shabbos window.
      return true;
    case RollType.ShabbosStrict:
      return (
        ctx.minutesOfWeek >= FRIDAY_1230_MINUTES_OF_WEEK &&
        ctx.minutesOfWeek < WEEK_END_MINUTES_OF_WEEK
      );
    case RollType.RoshChodesh:
      return ctx.isRoshChodesh;
    case RollType.YomTov:
      return ctx.isYomTov;
    case RollType.WeekdayCustom:
      // Real gating uses the roll's recurrence (selected weekdays + optional
      // time window) via isRollActiveNow; by type alone it counts as active.
      return true;
    case RollType.Custom:
    default:
      // Custom rolls are gated by their recurrence pattern, not by date math.
      return true;
  }
}

/** Parse an "HH:MM" clock string into minutes-of-day, or null when invalid. */
function parseClockMinutes(value: unknown): number | null {
  const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * One selected weekday's activation window inside a Weekday (Custom) roll:
 * either the whole day, or a start→end range expressed in minutes-of-day.
 */
export type WeekdayCustomDayWindow =
  | { fullDay: true }
  | { fullDay: false; start: number; end: number };

/**
 * The window a Weekday (Custom) roll is active in on `day` (Sun=0 … Sat=6).
 *
 * Per-day windows live in `recurrence.dayTimes`, keyed by the day number
 * (`{ 1: { fullDay, startTime, endTime } }`). A day marked `fullDay` — and any
 * day whose times are missing or unparseable, which is how rolls saved before
 * per-day windows existed read — falls back to the roll-wide startTime/endTime
 * and, failing that, to the whole day.
 */
export function resolveWeekdayCustomDayWindow(recurrence: any, day: number): WeekdayCustomDayWindow {
  const dayTimes = recurrence?.dayTimes;
  const entry = dayTimes ? (dayTimes[day] ?? dayTimes[String(day)]) : null;
  if (entry?.fullDay === true) return { fullDay: true };

  const start = parseClockMinutes(entry?.startTime) ?? parseClockMinutes(recurrence?.startTime);
  const end = parseClockMinutes(entry?.endTime) ?? parseClockMinutes(recurrence?.endTime);
  // Incomplete window → the roll simply runs all day on that weekday.
  if (start === null || end === null) return { fullDay: true };
  return { fullDay: false, start, end };
}

/** Selected weekdays (Sun=0 … Sat=6), de-duplicated and numeric. */
function getRecurrenceDays(recurrence: any): number[] {
  if (!Array.isArray(recurrence?.daysOfWeek)) return [];
  const days = recurrence.daysOfWeek
    .map((d: unknown) => Number(d))
    .filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6);
  return Array.from(new Set<number>(days));
}

/**
 * "Weekday (Custom)" scheduling: the roll is active on the chosen weekdays
 * (recurrence.daysOfWeek, Sun=0 … Sat=6) — the whole day when
 * `recurrence.timeMode` is 'fixed', or within each day's own start→end window
 * when it is 'range'. A window whose end is at or before its start
 * (e.g. 20:00 → 06:00) wraps past midnight into the next calendar day, so the
 * previous weekday's window is checked too.
 */
export function isWeekdayCustomActive(recurrence: any, ctx: RollContext): boolean {
  const days = getRecurrenceDays(recurrence);
  // No selected day → nothing to show.
  if (days.length === 0) return false;

  // 'fixed' (or unset) → active the whole day on each selected weekday.
  if (recurrence?.timeMode !== 'range') return days.includes(ctx.day);

  if (days.includes(ctx.day)) {
    const today = resolveWeekdayCustomDayWindow(recurrence, ctx.day);
    if (today.fullDay) return true;
    if (today.end > today.start) {
      if (ctx.minutesOfDay >= today.start && ctx.minutesOfDay < today.end) return true;
    } else if (ctx.minutesOfDay >= today.start) {
      // Wraps past midnight: [start, 24:00) still belongs to today.
      return true;
    }
  }

  // …and the tail [00:00, end) of a window that started on the previous day.
  const previousDay = (ctx.day + 6) % 7;
  if (days.includes(previousDay)) {
    const previous = resolveWeekdayCustomDayWindow(recurrence, previousDay);
    if (!previous.fullDay && previous.end <= previous.start && ctx.minutesOfDay < previous.end) {
      return true;
    }
  }

  return false;
}

/* -------------------------------------------------------------------------
 * Weekday (Custom) editor helpers
 *
 * The roll editors (Add / Edit / Duplicate) all drive the same inline schedule
 * widget, so the serialize + validate rules live here next to the read side
 * above — one place decides what a Weekday (Custom) recurrence looks like.
 * ---------------------------------------------------------------------- */

/** Weekday names indexed the way the recurrence stores them (Sun=0 … Sat=6). */
export const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** One weekday's persisted window inside a Weekday (Custom) recurrence. */
export interface WeekdayCustomDayTime {
  fullDay: boolean;
  startTime: string;
  endTime: string;
}

const DEFAULT_WINDOW_START = '09:00';
const DEFAULT_WINDOW_END = '17:00';

/** Normalize a clock value to "HH:MM", or null when it is not a valid time. */
function normalizeClockString(value: unknown): string | null {
  const minutes = parseClockMinutes(value);
  if (minutes === null) return null;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * The per-day windows a brand-new Weekday (Custom) roll starts with: every
 * weekday ticked "Full day", carrying the default window underneath so
 * unticking one gives a sensible start/end instead of a blank pair. Rolls
 * loaded from the database keep whatever they were saved with — this is only
 * the seed the Add/Duplicate editors hand the schedule widget.
 */
export function createDefaultWeekdayCustomDayTimes(): Record<string, WeekdayCustomDayTime> {
  const dayTimes: Record<string, WeekdayCustomDayTime> = {};
  for (let day = 0; day <= 6; day += 1) {
    dayTimes[String(day)] = {
      fullDay: true,
      startTime: DEFAULT_WINDOW_START,
      endTime: DEFAULT_WINDOW_END,
    };
  }
  return dayTimes;
}

/** The per-day entry stored for `day`, whichever key shape it was saved under. */
function getDayTimeEntry(schedule: any, day: number): any {
  const source = schedule?.dayTimes;
  if (!source || typeof source !== 'object') return {};
  return source[day] ?? source[String(day)] ?? {};
}

/**
 * Serialize the editor's Weekday (Custom) schedule into the `recurrence` shape
 * {@link isWeekdayCustomActive} reads. Per-day windows are written for every
 * weekday (not just the selected ones) so unchecking a day and checking it
 * again keeps the times that were set for it; `daysOfWeek` alone decides which
 * days actually run. Fixed (whole-day) rolls store no window at all.
 */
export function buildWeekdayCustomRecurrence(schedule: any): any {
  const days = getRecurrenceDays(schedule);
  const isRange = schedule?.timeMode === 'range';

  if (!isRange) {
    return {
      type: 'weekly',
      daysOfWeek: days,
      timeMode: 'fixed',
      startTime: null,
      endTime: null,
      dayTimes: null,
    };
  }

  // Roll-wide window: the fallback a day without its own times inherits.
  const fallbackStart = normalizeClockString(schedule?.startTime) ?? DEFAULT_WINDOW_START;
  const fallbackEnd = normalizeClockString(schedule?.endTime) ?? DEFAULT_WINDOW_END;

  const dayTimes: Record<string, WeekdayCustomDayTime> = {};
  for (let day = 0; day <= 6; day += 1) {
    const entry = getDayTimeEntry(schedule, day);
    dayTimes[String(day)] = {
      fullDay: entry?.fullDay === true,
      // Times are kept even for a "Full day" weekday, so unticking it later
      // restores the window instead of coming back blank.
      startTime: normalizeClockString(entry?.startTime) ?? fallbackStart,
      endTime: normalizeClockString(entry?.endTime) ?? fallbackEnd,
    };
  }

  return {
    type: 'weekly',
    daysOfWeek: days,
    timeMode: 'range',
    startTime: fallbackStart,
    endTime: fallbackEnd,
    dayTimes,
  };
}

/**
 * Guard the editor's Weekday (Custom) schedule before save: at least one active
 * weekday, and — in the start/end mode — a complete window on every active day
 * that is not marked "Full day". Returns the message to show the user, or null
 * when the schedule is good to save.
 */
export function validateWeekdayCustomSchedule(schedule: any): string | null {
  const days = getRecurrenceDays(schedule);
  if (days.length === 0) {
    return 'Select at least one active day for the Weekday (Custom) roll.';
  }
  if (schedule?.timeMode !== 'range') return null;

  const fallbackStart = normalizeClockString(schedule?.startTime);
  const fallbackEnd = normalizeClockString(schedule?.endTime);

  for (const day of days) {
    const entry = getDayTimeEntry(schedule, day);
    if (entry?.fullDay === true) continue;
    // A day that was never given its own times inherits the roll-wide window;
    // one whose field was cleared has to be filled back in.
    const start = entry?.startTime == null ? fallbackStart : normalizeClockString(entry.startTime);
    const end = entry?.endTime == null ? fallbackEnd : normalizeClockString(entry.endTime);
    if (start === null || end === null) {
      return `Set a start and end time for ${WEEKDAY_LABELS[day]}, or tick Full day.`;
    }
  }

  return null;
}

/**
 * Whether a roll is active right now. Weekday (Custom) rolls are gated by their
 * recurrence (selected weekdays + optional time window); every other type is
 * gated purely by {@link isRollActiveForType}.
 */
export function isRollActiveNow(roll: any, ctx: RollContext): boolean {
  if (normalizeRollType(roll?.type) === RollType.WeekdayCustom) {
    return isWeekdayCustomActive(roll?.recurrence, ctx);
  }
  return isRollActiveForType(roll?.type, ctx);
}

type GetMinutes = (minyan: any) => number;

function getRollMinyanim(roll: any): any[] {
  return Array.isArray(roll?.minyanim) ? roll.minyanim : [];
}

function getRollPriority(roll: any): number {
  const p = Number(roll?.priority);
  return Number.isFinite(p) ? p : 0;
}

function isTextMinyan(minyan: any): boolean {
  return minyan?.itemType === 'text' || minyan?.kind === 'text' || minyan?.type === 'text';
}

/**
 * The roll editor's per-minyan Active switch: switching a single row off hides
 * it from the display and the live preview while it stays in the roll.
 *
 * Absent = active, so every minyan saved before the switch existed — and every
 * minyan generated by an automated rule, which carries no such field — keeps
 * showing. Never test this with `=== true`.
 *
 * Deliberately not applied inside {@link getRollMinyanim}: that also feeds
 * {@link rollUsesManualOrder}, which must keep seeing the roll as it was
 * authored. The filtering happens in {@link buildSlotWinners} and
 * {@link getVisibleMinyanim} — after that ordering decision — plus at each
 * editor's live preview.
 */
export function isMinyanActive(minyan: any): boolean {
  return minyan?.isActive !== false;
}

/** A text block with "None (manual)" timing — it has no time to sort by and is
 * positioned purely by hand (drag order). */
function isManualTextMinyan(minyan: any): boolean {
  return isTextMinyan(minyan) && (
    minyan?.manual === true ||
    minyan?.timingMode === 'none' ||
    minyan?.textTiming?.timeType === 'none'
  );
}

/**
 * A roll whose minyanim are arranged by hand (drag order) rather than auto-
 * sorted by time: every Shabbos roll, plus any roll that contains a manual-time
 * text block. Their display/preview order follows each minyan's persisted
 * `order` field so the screen matches exactly how they were dragged in the editor.
 */
export function rollUsesManualOrder(roll: any): boolean {
  const type = roll?.type;
  if (type === 'shabbos' || type === 'shabbos_strict') return true;
  // Decided from the roll as it was AUTHORED, so a row switched off by the
  // per-minyan Active toggle still counts. Otherwise hiding the one manual-time
  // text block would silently re-sort the whole roll by time on screen while the
  // editor and its live preview — which both read the unfiltered list — kept
  // showing the drag order. Callers that hand us an already-filtered `minyanim`
  // pass the authored list as `_orderSourceMinyanim`.
  const source = Array.isArray(roll?._orderSourceMinyanim)
    ? roll._orderSourceMinyanim
    : getRollMinyanim(roll);
  return source.some(isManualTextMinyan);
}

/** Persisted drag position; minyanim without an explicit order fall to the end
 * (e.g. automated/generated minyanim appended after the manual ones). */
function getMinyanOrder(minyan: any): number {
  const n = Number(minyan?.order);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

/**
 * Keep only the rolls that are active right now and (optionally) honour their
 * `isActive` flag. Returned in descending priority order so the highest
 * priority is considered first.
 */
export function getActiveRolls(rolls: any[], ctx: RollContext): any[] {
  return (Array.isArray(rolls) ? rolls : [])
    .filter((roll) => roll && roll.isActive !== false && isRollActiveNow(roll, ctx))
    .sort((a, b) => getRollPriority(b) - getRollPriority(a));
}

/**
 * Priority "Greatest Wins": across all active rolls, record the highest
 * priority that claims each time slot (minutes-of-day). A minyan is later
 * hidden if its roll's priority is below the winner for its slot.
 *
 * A minyan switched off ({@link isMinyanActive}) claims nothing: it is not on
 * screen, so it must not suppress a lower-priority roll's minyan at the same
 * time.
 */
export function buildSlotWinners(activeRolls: any[], getMinutes: GetMinutes): Map<number, number> {
  const winners = new Map<number, number>();
  for (const roll of activeRolls) {
    const priority = getRollPriority(roll);
    for (const minyan of getRollMinyanim(roll)) {
      if (!isMinyanActive(minyan)) continue;
      const minutes = getMinutes(minyan);
      if (!Number.isFinite(minutes)) continue;
      const current = winners.get(minutes);
      if (current === undefined || priority > current) winners.set(minutes, priority);
    }
  }
  return winners;
}

/**
 * Remap a minutes-of-day value onto the roll display's day, which starts at
 * {@link ROLL_DAY_START_MINUTES} (2:00 AM) rather than at midnight:
 * 2:00 AM → 0, 7:00 AM → 300, midnight → 1320, 1:59 AM → 1439. Sorting by this
 * key is what pushes pre-2:00 AM minyanim to the very end of a roll.
 *
 * Values outside [0, 1440) wrap first, so a time rounded up to 24:00 sorts where
 * its displayed 12:00 AM clock says it belongs. Times that cannot be resolved
 * (Infinity / NaN) stay Infinity so they still sink last.
 *
 * Ordering only — a minyan's stored time and the clock shown for it are never
 * touched by this.
 */
export function toRollDaySortKey(totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return Number.POSITIVE_INFINITY;
  const minutesOfDay = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return (minutesOfDay - ROLL_DAY_START_MINUTES + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

/**
 * Comparator for two resolved minutes-of-day values on the roll's 2:00 AM day
 * ({@link toRollDaySortKey}). Unresolvable times compare equal to one another
 * and after every real time, so an Infinity/NaN can never return NaN and
 * corrupt the sort. Equal times compare 0, which keeps `Array#sort` stable — the
 * incoming order (drag order / generation order) breaks the tie.
 *
 * The single comparator behind the live display, the roll editor's live preview
 * and the dashboard's embedded screen preview, so all three order identically.
 */
export function compareRollDayMinutes(a: number, b: number): number {
  const aKey = toRollDaySortKey(a);
  const bKey = toRollDaySortKey(b);
  if (aKey === bKey) return 0;
  if (!Number.isFinite(aKey)) return 1;
  if (!Number.isFinite(bKey)) return -1;
  return aKey - bKey;
}

/**
 * Visible minyanim for a single roll after conflict resolution: a minyan is
 * dropped when a higher-priority active roll also has a minyan at the same
 * slot. Minyanim whose time cannot be resolved are always kept.
 *
 * Order: manually-arranged rolls (Shabbos, or any roll with a manual text
 * block) keep the hand-picked drag order (the persisted `order` field) so the
 * display matches the editor exactly; all other rolls are sorted by time on the
 * 2:00 AM day (see {@link compareRollDayMinutes}), so a 1:59 AM minyan lands at
 * the end of the roll instead of the start.
 */
export function getVisibleMinyanim(roll: any, slotWinners: Map<number, number>, getMinutes: GetMinutes): any[] {
  const priority = getRollPriority(roll);
  const visible = getRollMinyanim(roll)
    .filter((minyan) => {
      // Switched off in the roll editor: kept in the roll, never on screen.
      if (!isMinyanActive(minyan)) return false;
      const minutes = getMinutes(minyan);
      if (!Number.isFinite(minutes)) return true;
      const winner = slotWinners.get(minutes);
      // Greatest wins; ties keep the minyan (equal-priority rolls don't hide
      // each other).
      return winner === undefined || priority >= winner;
    });

  if (rollUsesManualOrder(roll)) {
    return visible.sort((a, b) => getMinyanOrder(a) - getMinyanOrder(b));
  }
  return visible.sort((a, b) => compareRollDayMinutes(getMinutes(a), getMinutes(b)));
}

/**
 * One-shot resolver: given an artboard's rolls and a context, return the
 * active rolls (with their visible, conflict-resolved minyanim attached) that
 * actually have something to show. Convenient for the live display.
 */
export function resolveRollDisplay(
  rolls: any[],
  ctx: RollContext,
  getMinutes: GetMinutes
): Array<{ roll: any; minyanim: any[] }> {
  const activeRolls = getActiveRolls(rolls, ctx);
  const slotWinners = buildSlotWinners(activeRolls, getMinutes);
  return activeRolls
    .map((roll) => ({ roll, minyanim: getVisibleMinyanim(roll, slotWinners, getMinutes) }))
    .filter((entry) => entry.minyanim.length > 0);
}

export type SecondaryTimeKey = 'bruches' | 'hoidy' | 'ashrei' | 'borchy' | 'netz';

/** The point in a minyan that the roll-wide `{countdown}` variable targets. */
export type RollCountdownTarget = 'minyan' | 'bruches' | 'hoidy' | 'netz';

/** Options shown by the Highlight & Countdown card, in display order. */
export const ROLL_COUNTDOWN_TARGET_OPTIONS: ReadonlyArray<{
  value: RollCountdownTarget;
  label: string;
}> = [
  { value: 'minyan', label: 'Next Minyan' },
  { value: 'bruches', label: 'Bruches' },
  { value: 'hoidy', label: 'Hoidy' },
  { value: 'netz', label: 'Netz' },
];

const ROLL_COUNTDOWN_TARGET_VALUES = new Set<string>(
  ROLL_COUNTDOWN_TARGET_OPTIONS.map((option) => option.value)
);

/**
 * Normalize a saved countdown target. Rolls created before target selection was
 * added (and malformed values) retain the original next-minyan behaviour.
 */
export function normalizeRollCountdownTarget(value: unknown): RollCountdownTarget {
  const normalized = String(value ?? '').trim().toLowerCase();
  return ROLL_COUNTDOWN_TARGET_VALUES.has(normalized)
    ? normalized as RollCountdownTarget
    : 'minyan';
}

/**
 * What a secondary time's offset is measured from:
 *   'minyan'  → the minyan's own start time (a davening milestone).
 *   'sunrise' → the day's real Netz (sunrise) zman, which the display layer
 *               resolves from the screen's location and injects as an anchor.
 */
export type SecondaryTimeAnchor = 'minyan' | 'sunrise';

/** The zman key Netz anchors to (kosher-zmanim's sunrise). */
export const NETZ_ZMAN_KEY = 'sunrise';

export interface MinyanDisplayToggleDef {
  key: SecondaryTimeKey;
  /** The minyan/rule field holding this toggle's offset, e.g. `bruchesOffset`. */
  offsetKey: string;
  /** Hebrew label — shown on the toggle card AND next to the time on screen. */
  label: string;
  /** English label used in the design tab's Variable menus. */
  variableLabel: string;
  /** Offset presets offered as chips (0 renders as "Exact"). */
  presets: number[];
  /** Offset used when nothing valid is stored. */
  default: number;
  /**
   * Smallest offset the editor accepts — 0 lets a toggle sit exactly on its
   * anchor, a negative one lets it come before it.
   */
  minOffset: number;
  anchor: SecondaryTimeAnchor;
  /**
   * Hidden behind the editor's "+N more" reveal instead of shown up-front. Keeps
   * the toggle row to the three everyday milestones while the rest stay one
   * click away. Display behaviour is identical either way.
   */
  collapsed?: boolean;
}

/**
 * THE source of truth for the roll "Display Toggles". Everything that touches a
 * secondary time — the Minyanim tab cards, the Automated-rule cards, the design
 * tab's variable menus, the `{token}` interpolation, the live display, the editor
 * preview and the dashboard — derives from this table, so a new toggle only ever
 * has to be added here (plus its two columns on the backend entity).
 */
export const MINYAN_DISPLAY_TOGGLES: MinyanDisplayToggleDef[] = [
  {
    key: 'bruches',
    offsetKey: 'bruchesOffset',
    label: 'ברכות',
    variableLabel: 'Bruches',
    presets: [5, 10],
    default: BRUCHES_OFFSET_MINUTES,
    // Bruches can start before the minyan's listed time, so a custom offset may
    // be negative (-X = X minutes before) or 0, as far back as a full day. The
    // backend's minyan cast keeps the same floor.
    minOffset: -MINUTES_PER_DAY,
    anchor: 'minyan',
  },
  {
    key: 'hoidy',
    offsetKey: 'hoidyOffset',
    label: 'הודו',
    variableLabel: 'Hoidy',
    presets: [10, 15],
    default: HOIDY_OFFSET_MINUTES,
    minOffset: 1,
    anchor: 'minyan',
  },
  {
    key: 'ashrei',
    offsetKey: 'ashreiOffset',
    label: 'אשרי',
    variableLabel: 'Ashrei',
    presets: [3, 5],
    default: ASHREI_OFFSET_MINUTES,
    minOffset: 1,
    anchor: 'minyan',
  },
  {
    key: 'borchy',
    offsetKey: 'borchyOffset',
    label: 'ברכו',
    variableLabel: 'Borchy',
    presets: [20, 25],
    default: BORCHY_OFFSET_MINUTES,
    minOffset: 1,
    anchor: 'minyan',
    collapsed: true,
  },
  {
    // Netz is not a davening milestone but the sunrise itself, so it anchors to
    // the real zman for the day rather than to the minyan's start time. The
    // offset shifts it off sunrise, and defaults to Exact (0).
    key: 'netz',
    offsetKey: 'netzOffset',
    label: 'נץ החמה',
    variableLabel: 'Netz',
    presets: [0, 1, 2],
    default: NETZ_OFFSET_MINUTES,
    minOffset: 0,
    anchor: 'sunrise',
    collapsed: true,
  },
];

/** Toggles shown up-front on a toggle row. */
export const MINYAN_DISPLAY_TOGGLES_PRIMARY = MINYAN_DISPLAY_TOGGLES.filter((def) => !def.collapsed);
/** Toggles revealed by the row's "+N more" button. */
export const MINYAN_DISPLAY_TOGGLES_EXTRA = MINYAN_DISPLAY_TOGGLES.filter((def) => def.collapsed);

/** Look up a toggle definition by key (undefined for an unknown key). */
export function getMinyanDisplayToggleDef(key: unknown): MinyanDisplayToggleDef | undefined {
  return MINYAN_DISPLAY_TOGGLES.find((def) => def.key === key);
}

/** The chip/menu text for an offset — "Exact" at 0, otherwise signed: "+5" / "-5". */
export function formatToggleOffsetLabel(offsetMinutes: unknown): string {
  const n = Number(offsetMinutes);
  if (!Number.isFinite(n) || n === 0) return 'Exact';
  return n > 0 ? `+${n}` : String(n);
}

/**
 * An offset as a number. Only numbers and non-blank strings count: blank, null
 * and the like are "unset", never 0 — 0 is a real Bruches offset, so reading a
 * missing one as 0 would move it onto the minyan's start instead of its default.
 */
function toToggleOffsetNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return Number(value);
  return Number.NaN;
}

/** A stored offset, falling back to the toggle's default when it is unusable. */
export function resolveMinyanToggleOffset(
  value: unknown,
  def: Pick<MinyanDisplayToggleDef, 'default' | 'minOffset'>
): number {
  const n = toToggleOffsetNumber(value);
  return Number.isFinite(n) && n >= def.minOffset ? n : def.default;
}

/**
 * Whether an offset typed into a toggle card's Custom box can be saved: whole
 * minutes (the backend column is an integer) no smaller than the toggle's
 * `minOffset`. A half-typed value — "" or a lone "-" — is not.
 */
export function isValidMinyanToggleOffset(
  value: unknown,
  def: Pick<MinyanDisplayToggleDef, 'minOffset'>
): boolean {
  const n = toToggleOffsetNumber(value);
  return Number.isInteger(n) && n >= def.minOffset;
}

/**
 * Every toggle field for a minyan / automated rule, normalized. Used both when
 * seeding a new row (pass nothing → all off, default offsets) and when reading
 * or writing a saved one, so the shape is identical everywhere.
 */
export function normalizeMinyanDisplayToggles(source?: any): Record<string, boolean | number> {
  const out: Record<string, boolean | number> = {};
  for (const def of MINYAN_DISPLAY_TOGGLES) {
    out[def.key] = Boolean(source?.[def.key]);
    out[def.offsetKey] = resolveMinyanToggleOffset(source?.[def.offsetKey], def);
  }
  return out;
}

export interface SecondaryTime {
  key: SecondaryTimeKey;
  label: string;
  offsetMinutes: number;
  anchor: SecondaryTimeAnchor;
}

/** The anchors a secondary time can be measured from, in minutes-of-day. */
export interface SecondaryTimeAnchors {
  /** The minyan's own start time. */
  minyanMinutes?: number | null;
  /** The day's Netz (sunrise) for the screen's location. */
  sunriseMinutes?: number | null;
}

export interface ResolvedSecondaryTime {
  key: SecondaryTimeKey;
  label: string;
  /** Minutes-of-day of the resolved time. */
  minutes: number;
  /** The same value as a 12-hour clock string, e.g. "7:05". */
  time: string;
}

/**
 * Which secondary timestamps a minyan should display, based on its toggles.
 * Each entry carries the offset AND what that offset is measured from — the
 * minyan's start time for the davening milestones, the day's sunrise for Netz.
 */
export function getMinyanSecondaryTimes(minyan: any): SecondaryTime[] {
  return MINYAN_DISPLAY_TOGGLES
    .filter((def) => Boolean(minyan?.[def.key]))
    .map((def) => ({
      key: def.key,
      label: def.label,
      offsetMinutes: resolveMinyanToggleOffset(minyan?.[def.offsetKey], def),
      anchor: def.anchor,
    }));
}

/** One secondary time as minutes-of-day, or null when its anchor is unavailable. */
export function resolveSecondaryTimeMinutes(
  secondary: SecondaryTime,
  anchors: SecondaryTimeAnchors
): number | null {
  const base = secondary.anchor === 'sunrise' ? anchors?.sunriseMinutes : anchors?.minyanMinutes;
  if (!Number.isFinite(base as number)) return null;
  return (base as number) + secondary.offsetMinutes;
}

/**
 * Put a recurring clock time in [0, 1440) without dropping the fractional
 * minute carried by a Show Seconds minyan. Blank/null anchors stay invalid.
 */
function normalizeRollCountdownMinutes(value: unknown): number {
  if (value === null || value === undefined || value === '') return Number.NaN;
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return Number.NaN;
  return ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

/**
 * Resolve one minyan's configured countdown point as minutes-of-day.
 *
 * Unlike {@link resolveMinyanSecondaryTimes}, this intentionally does not read
 * the secondary time's visibility toggle. Countdown To is a roll-wide setting:
 * its milestone remains usable even when that timestamp is not rendered beside
 * the minyan. Stored per-minyan offsets still apply, with the existing defaults
 * used for missing/invalid values.
 *
 * Netz needs the day's sunrise anchor. If it is temporarily unavailable (for
 * example while location zmanim load), fall back to the primary minyan time so
 * the countdown remains useful instead of disappearing. Invalid/legacy targets
 * are normalized to that same primary-minyan behaviour.
 */
export function resolveRollCountdownTargetMinutes(
  minyan: any,
  target: unknown,
  anchors: SecondaryTimeAnchors
): number {
  const primaryMinutes = normalizeRollCountdownMinutes(anchors?.minyanMinutes);
  const normalizedTarget = normalizeRollCountdownTarget(target);
  if (normalizedTarget === 'minyan') return primaryMinutes;

  const def = getMinyanDisplayToggleDef(normalizedTarget);
  if (!def) return primaryMinutes;

  const anchor = def.anchor === 'sunrise'
    ? anchors?.sunriseMinutes
    : anchors?.minyanMinutes;
  const anchorMinutes = normalizeRollCountdownMinutes(anchor);
  if (!Number.isFinite(anchorMinutes)) return primaryMinutes;

  const offset = resolveMinyanToggleOffset(minyan?.[def.offsetKey], def);
  return normalizeRollCountdownMinutes(anchorMinutes + offset);
}

/**
 * A minyan's enabled secondary times, resolved to real clock times against the
 * supplied anchors. Entries whose anchor can't be resolved (e.g. Netz before the
 * screen's zmanim have loaded) are dropped rather than shown as "--:--", so the
 * live display, the editor preview and the dashboard all render the same list.
 */
export function resolveMinyanSecondaryTimes(
  minyan: any,
  anchors: SecondaryTimeAnchors
): ResolvedSecondaryTime[] {
  const out: ResolvedSecondaryTime[] = [];
  for (const secondary of getMinyanSecondaryTimes(minyan)) {
    const minutes = resolveSecondaryTimeMinutes(secondary, anchors);
    if (minutes === null) continue;
    out.push({
      key: secondary.key,
      label: secondary.label,
      minutes,
      time: formatClockFromMinutes(minutes),
    });
  }
  return out;
}

/* -------------------------------------------------------------------------
 * Roll design variables ({token} placeholders)
 * ---------------------------------------------------------------------- */

/** The non-toggle variables every roll design offers. */
const ROLL_BASE_VARIABLES: Array<{ value: string; label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'room', label: 'Room' },
  { value: 'time', label: 'Time' },
  { value: 'countdown', label: 'Countdown' },
];

/**
 * The Variable menu shown in the design tab (Grid Designer, Upcoming editor) and
 * the List-view field pickers — the base fields plus one entry per display
 * toggle, in the same order the toggle cards appear.
 */
export const ROLL_DESIGN_VARIABLES: Array<{ value: string; label: string }> = [
  ...ROLL_BASE_VARIABLES,
  ...MINYAN_DISPLAY_TOGGLES.map((def) => ({ value: def.key, label: def.variableLabel })),
];

/**
 * Every `{token}` a roll design cell can contain (`minyan` aliases `name`), plus
 * the roll-wide `parsha` — the week's parsha, exactly as the Date/Time banner
 * shows it — which the Add/Edit Text modals offer.
 */
const ROLL_FIELD_TOKENS = [
  'name',
  'minyan',
  ...ROLL_DESIGN_VARIABLES.map((v) => v.value).filter((v) => v !== 'name'),
  'parsha',
];
const ROLL_FIELD_TOKEN_PATTERN = `\\{(${ROLL_FIELD_TOKENS.join('|')})\\}`;

/** Matches a single `{token}` — for "does this cell have content?" checks. */
export const ROLL_FIELD_TOKEN_REGEX = new RegExp(ROLL_FIELD_TOKEN_PATTERN);
/** Global variant for interpolation. Safe to share: `String.replace` resets lastIndex. */
export const ROLL_FIELD_TOKEN_REGEX_GLOBAL = new RegExp(ROLL_FIELD_TOKEN_PATTERN, 'g');

/** Format minutes-of-day as a 12-hour clock string, e.g. 425 → "7:05". */
export function formatClockFromMinutes(totalMinutes: number, options: { withPeriod?: boolean } = {}): string {
  if (!Number.isFinite(totalMinutes)) return '--:--';
  const normalized = ((Math.round(totalMinutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours24 = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const hours12 = hours24 % 12 || 12;
  const base = `${hours12}:${String(minutes).padStart(2, '0')}`;
  if (!options.withPeriod) return base;
  return `${base} ${hours24 >= 12 ? 'PM' : 'AM'}`;
}

/** Milliseconds until the next occurrence of a minutes-of-day target. */
export function getRollCountdownDiffMs(now: Date, targetMinutes: number): number {
  if (!now || !Number.isFinite(targetMinutes)) return Number.NaN;
  const current = now instanceof Date ? now : new Date(now);
  const currentMs = current.getTime();
  if (!Number.isFinite(currentMs)) return Number.NaN;

  const target = new Date(current);
  target.setHours(0, 0, 0, 0);
  target.setMinutes(Math.round(targetMinutes), 0, 0);

  if (target.getTime() <= currentMs) {
    target.setDate(target.getDate() + 1);
  }

  return target.getTime() - currentMs;
}

/** Format a countdown as HH:MM:SS. Hours are not capped. */
export function formatRollCountdown(totalSeconds: number): string {
  const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/* =========================================================================
 * Automated Minyan Generator
 * -------------------------------------------------------------------------
 * Expands a single Automated Rule into concrete minyan items, implementing
 * the spec:
 *   1. Schedule Anchor (Window)  — start/end bounds + interval gap.
 *   2. Time-Snap & Rounding      — snap_grid = currentTime.roundTo(grid),
 *                                  with the "Exact Start" exception.
 *   4. Fixed Pattern Sync        — room_index =
 *                                  floor(total_minutes_since_midnight / interval) % room_pool.length
 *      (when disabled, rooms rotate sequentially in priority order).
 *
 * Zmanim resolution lives in the display layer, so the caller injects
 * `resolveBoundaryMinutes(boundary)` which turns a saved AutomatedTimeBoundary
 * into minutes-of-day (or null when it cannot be resolved).
 * ====================================================================== */

export interface AutomatedRoom {
  id: string;
  name: string;
}

export interface GeneratedMinyan {
  id: string;
  /** Display label (room name when a room pool is set, else the minyan name). */
  name: string;
  minyanName: string;
  roomName: string | null;
  roomId: string | null;
  roomLocation?: string;
  timingMode: 'fixed';
  fixedTime: string; // 24h "HH:mm"
  /** Display toggles inherited from the rule (one `<key>` + `<key>Offset` pair
   *  per entry in {@link MINYAN_DISPLAY_TOGGLES}), in the same shape as a
   *  manually-defined minyan so the display layer treats both identically
   *  (see {@link getMinyanSecondaryTimes}). */
  [toggleField: string]: any;
  __generated: true;
  __ruleId: string;
}

export interface AutomatedGenerateOptions {
  /** Resolve a saved AutomatedTimeBoundary to minutes-of-day [0,1440) or null. */
  resolveBoundaryMinutes: (boundary: any) => number | null;
  /** Hard cap on generated minyanim per rule (safety). Defaults to 200. */
  maxCount?: number;
}

/**
 * Round a minutes value to the nearest multiple of `grid` (the shared "Round
 * Start Time To" snap used by Automated rules and by Shabbos dynamic minyanim).
 * A non-positive grid (Exact) leaves the value untouched.
 */
export function roundMinutesToNearest(totalMinutes: number, grid: number): number {
  if (!Number.isFinite(totalMinutes) || !grid || grid <= 0) return totalMinutes;
  return Math.round(totalMinutes / grid) * grid;
}

/**
 * A minyan's "Round Start Time To" grid in minutes (0 = Exact / no rounding).
 * Meaningful for Shabbos-roll dynamic-zman minyanim and for Weekly Static Zman
 * minyanim (any roll type), where the resolved zman time is snapped to this
 * grid on the display, the editor preview, sorting, secondary times and the
 * countdown.
 */
export function getMinyanRoundToMinutes(minyan: any): number {
  const grid = Number(minyan?.roundToMinutes);
  return Number.isFinite(grid) && grid > 0 ? grid : 0;
}

/* -------------------------------------------------------------------------
 * Weekly Static Zman timing mode
 * -------------------------------------------------------------------------
 * A minyan on this mode resolves its zman for EVERY day of its week — Sunday
 * through Friday, from Sunday midnight to Friday midnight (see
 * {@link getWeeklyZmanWeekDates}) — then shows the minimum or maximum of those
 * six clock times: one static time for the whole week. That time is then
 * shifted by the minyan's Offset (± minutes) and only afterwards snapped by an
 * optional "Round Time To" grid in the minyan's "Round To" direction (closer /
 * high / lower) — see {@link resolveWeeklyZmanMinutes}.
 */

/** Canonical stored value for the Weekly Static Zman timing mode. */
export const WEEKLY_ZMAN_TIMING_MODE = 'weeklyZman';

/** True when a stored timingMode means "Weekly Static Zman" (any variant). */
export function isWeeklyZmanTimingMode(mode: unknown): boolean {
  const raw = String(mode ?? '').trim().toLowerCase();
  return raw === 'weeklyzman' || raw === 'weekly_zman' || raw === 'weekly-zman' || raw === 'weekly';
}

/** The minyan's Max/Min selection for Weekly Static Zman ('max' by default). */
export function getMinyanWeeklyZmanMode(minyan: any): 'max' | 'min' {
  return minyan?.weeklyZmanMode === 'min' ? 'min' : 'max';
}

// Sunday through Friday.
const WEEKLY_ZMAN_WEEK_DAYS = 6;

/**
 * The dates a Weekly Static Zman week scans, for the org-local calendar date
 * `civilDate` (a Date at local noon, as the display's getScreenCivilDate builds
 * it): Sunday through Friday of the week that date is in — from Sunday midnight
 * to Friday midnight. Shabbos is not scanned; it belongs to the week that began
 * the Sunday before it, so a week's time holds until the next Sunday midnight.
 * Each date keeps `civilDate`'s noon, so a zmanim calendar set to it reads the
 * intended day whatever the DST offset. The live display and the editor
 * preview both scan exactly these dates.
 */
export function getWeeklyZmanWeekDates(civilDate: Date): Date[] {
  const sunday = new Date(civilDate.getTime());
  sunday.setDate(civilDate.getDate() - civilDate.getDay());
  return Array.from({ length: WEEKLY_ZMAN_WEEK_DAYS }, (_, index) => {
    const date = new Date(sunday.getTime());
    date.setDate(sunday.getDate() + index);
    return date;
  });
}

/**
 * "Round To" direction for the grid snap, mirroring the Zmanim override values:
 *   closer → nearest grid mark (midpoint rounds up)   [default]
 *   high   → always up to the next grid mark
 *   lower  → always down to the previous grid mark
 */
export const MINYAN_ROUND_DIRECTIONS = {
  closer: 'round to closer',
  high: 'round to high',
  lower: 'round to lower',
} as const;

/** A minyan's stored "Round To" direction, defaulting to `closer`. */
export function getMinyanRoundDirection(minyan: any): string {
  const dir = minyan?.roundDirection;
  return dir === MINYAN_ROUND_DIRECTIONS.high || dir === MINYAN_ROUND_DIRECTIONS.lower
    ? dir
    : MINYAN_ROUND_DIRECTIONS.closer;
}

/**
 * Snap a minutes value to `grid` in the requested direction. A non-positive
 * grid (Exact) leaves the value untouched; an unknown/empty direction rounds to
 * the nearest mark (same as {@link roundMinutesToNearest}).
 */
export function roundMinutesToGridDirectional(
  totalMinutes: number,
  grid: number,
  direction?: string,
): number {
  if (!Number.isFinite(totalMinutes) || !grid || grid <= 0) return totalMinutes;
  if (direction === MINYAN_ROUND_DIRECTIONS.lower) return Math.floor(totalMinutes / grid) * grid;
  if (direction === MINYAN_ROUND_DIRECTIONS.high) return Math.ceil(totalMinutes / grid) * grid;
  return roundMinutesToNearest(totalMinutes, grid);
}

/**
 * Apply a minyan's own "Round Time To" grid + "Round To" direction to a resolved
 * minutes-of-day value. The single place that pairs the two settings, so the
 * editor preview, the live display and the dashboard preview all snap a Weekly
 * Static Zman time identically. Grid 0 (Exact) returns the value untouched.
 *
 * e.g. 2:17 → :05 lower = 2:15, high = 2:20; :10 lower = 2:10, high = 2:20.
 *
 * Rounding only — Weekly Static Zman consumers must call
 * {@link resolveWeeklyZmanMinutes} instead, which applies the minyan's Offset
 * first and then rounds.
 */
export function roundMinyanZmanMinutes(minyan: any, totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return totalMinutes;
  const grid = getMinyanRoundToMinutes(minyan);
  if (grid <= 0) return totalMinutes;
  return roundMinutesToGridDirectional(totalMinutes, grid, getMinyanRoundDirection(minyan));
}

/** Wrap a minutes value into [0, 1440) so a ± offset that crosses midnight lands
 *  on the real clock time (and sorts there) instead of running out of range. */
function normalizeMinutesOfDay(totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return totalMinutes;
  return ((Math.round(totalMinutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

/**
 * A minyan's zman Offset in minutes: positive shifts the resolved time later,
 * negative earlier, 0 when unset. Shared by Dynamic Zman and Weekly Static Zman
 * (both store it on `offsetMinutes`), so the two modes read it identically.
 */
export function getMinyanOffsetMinutes(minyan: any): number {
  const raw = (
    minyan?.offsetMinutes ??
    minyan?.zmanimOffsetMinutes ??
    minyan?.zmanim_offset_minutes ??
    minyan?.timing?.zmanim?.offsetMinutes ??
    minyan?.timing?.zman?.offsetMinutes ??
    0
  );
  const offset = Number(raw);
  return Number.isFinite(offset) ? offset : 0;
}

/**
 * The full Weekly Static Zman pipeline for a resolved weekly Min/Max
 * minutes-of-day: apply the minyan's Offset FIRST, then snap the result to its
 * "Round Time To" grid in its "Round To" direction. The one place that fixes
 * that order, so the editor preview, the live display and the dashboard preview
 * all land on the same clock, sort key, secondary times and countdown.
 *
 * e.g. weekly Max 6:57 with Offset +5 → 7:02, then :10 lower = 7:00 / high = 7:10.
 */
export function resolveWeeklyZmanMinutes(minyan: any, totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return totalMinutes;
  const shifted = normalizeMinutesOfDay(totalMinutes + getMinyanOffsetMinutes(minyan));
  return roundMinyanZmanMinutes(minyan, shifted);
}

/** An Offset as a summary suffix: 5 → "+5 min", -5 → "-5 min", 0 → "". */
function formatMinyanOffsetLabel(offsetMinutes: unknown): string {
  const offset = Number(offsetMinutes);
  if (!Number.isFinite(offset) || offset === 0) return '';
  return `${offset > 0 ? '+' : ''}${offset} min`;
}

/**
 * Editor/summary label for a Weekly Static Zman minyan whose real clock time
 * isn't resolvable yet (no location/zmanim data), e.g.
 * "Alos 72 (Weekly Max +5 min)".
 */
export function getWeeklyZmanSummaryLabel(zmanLabel: string, minyan: any): string {
  const mode = getMinyanWeeklyZmanMode(minyan) === 'min' ? 'Min' : 'Max';
  const offset = formatMinyanOffsetLabel(getMinyanOffsetMinutes(minyan));
  return `${zmanLabel} (Weekly ${mode}${offset ? ` ${offset}` : ''})`;
}

/** snap_grid: currentTime.roundTo(gridValue) — snaps to the nearest grid mark. */
function roundMinutesToGrid(totalMinutes: number, grid: number): number {
  return roundMinutesToNearest(totalMinutes, grid);
}

/** Format minutes-of-day as a 24h "HH:mm" string (wraps across midnight). */
function clock24FromMinutes(totalMinutes: number): string {
  const normalized = ((Math.round(totalMinutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Normalize a stored room pool (ids or {id,name} objects) to {id,name}. */
function normalizeRoomPool(rooms: any): AutomatedRoom[] {
  if (!Array.isArray(rooms)) return [];
  return rooms
    .map((room): AutomatedRoom | null => {
      if (room && typeof room === 'object') {
        const id = String(room.id ?? room._id ?? room.name ?? '');
        const name = String(room.name ?? room.id ?? room._id ?? '');
        if (!id && !name) return null;
        return { id, name };
      }
      const value = String(room ?? '').trim();
      if (!value) return null;
      return { id: value, name: value };
    })
    .filter((room): room is AutomatedRoom => room !== null);
}

/** The minyan name for a rule (handles the `custom` name type). */
export function getAutomatedRuleMinyanName(rule: any): string {
  if (!rule) return '';
  if (rule.nameType === 'custom') return rule.customName || rule.name || 'Custom';
  return rule.nameType || rule.name || '';
}

/** The rounding grid (minutes) for a rule, or 0 when no rounding applies. */
function getRuleGrid(rule: any): number {
  const grid = Number(rule?.roundToMinutes);
  return Number.isFinite(grid) && grid > 0 ? grid : 0;
}

/**
 * Expand one Automated Rule into concrete, fixed-time minyan items.
 * Returns an empty array when the rule is disabled or its window cannot be
 * resolved (e.g. a zman that is not available for the current day).
 */
export function generateAutomatedMinyanim(rule: any, options: AutomatedGenerateOptions): GeneratedMinyan[] {
  if (!rule || rule.isEnabled === false) return [];

  const interval = Number(rule.intervalMinutes ?? rule.interval);
  if (!Number.isFinite(interval) || interval <= 0) return [];

  const startMinutes = options.resolveBoundaryMinutes(rule.startTime);
  if (startMinutes === null || !Number.isFinite(startMinutes)) return [];

  let endMinutes = options.resolveBoundaryMinutes(rule.endTime);
  if (endMinutes === null || !Number.isFinite(endMinutes)) return [];
  if (endMinutes < startMinutes) endMinutes += MINUTES_PER_DAY; // window crosses midnight

  const grid = getRuleGrid(rule);
  const exactStart = Boolean(rule.exactStart);
  const fixedPatternSync = Boolean(rule.fixedPatternSync);
  const pool = normalizeRoomPool(rule.rooms);
  const minyanName = getAutomatedRuleMinyanName(rule);
  const maxCount = Number.isFinite(options.maxCount as number) && (options.maxCount as number) > 0
    ? Math.floor(options.maxCount as number)
    : 200;

  // Display toggles are configured once on the rule and inherited by every
  // generated minyan, exactly like a manually-defined minyan's toggles.
  const displayToggles = normalizeMinyanDisplayToggles(rule);

  const out: GeneratedMinyan[] = [];
  let lastSnapped: number | null = null;

  for (let i = 0; i < 1000; i++) {
    const raw = startMinutes + i * interval;
    if (raw > endMinutes) break;

    // "Exact Start" exception: when enabled the very first minyan keeps the
    // anchor; every later minyan snaps to the grid. When disabled, even the
    // first snaps.
    let snapped: number;
    if (grid > 0 && !(i === 0 && exactStart)) {
      snapped = roundMinutesToGrid(raw, grid);
    } else {
      snapped = raw;
    }

    if (snapped > endMinutes) break;
    if (snapped === lastSnapped) continue; // skip duplicate slot after snapping

    let roomName: string | null = null;
    let roomId: string | null = null;
    if (pool.length > 0) {
      let index: number;
      if (fixedPatternSync) {
        // Ghost Grid: room_index = (total_minutes_since_midnight / interval) % pool.length
        const minutesSinceMidnight = ((Math.round(snapped) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
        index = Math.floor(minutesSinceMidnight / interval) % pool.length;
      } else {
        index = out.length % pool.length; // sequential rotation in priority order
      }
      index = ((index % pool.length) + pool.length) % pool.length;
      roomName = pool[index].name;
      roomId = pool[index].id;
    }

    out.push({
      id: `${rule.id || 'auto'}-${i}`,
      name: roomName || minyanName,
      minyanName,
      roomName,
      roomId,
      roomLocation: roomName || undefined,
      timingMode: 'fixed',
      fixedTime: clock24FromMinutes(snapped),
      ...displayToggles,
      __generated: true,
      __ruleId: String(rule.id || ''),
    });
    lastSnapped = snapped;

    if (out.length >= maxCount) break;
  }

  return out;
}

/** Expand all enabled automated rules on a roll into generated minyan items. */
export function generateRollAutomatedMinyanim(roll: any, options: AutomatedGenerateOptions): GeneratedMinyan[] {
  const rules = Array.isArray(roll?.automatedRules) ? roll.automatedRules : [];
  const out: GeneratedMinyan[] = [];
  for (const rule of rules) {
    out.push(...generateAutomatedMinyanim(rule, options));
  }
  return out;
}
